/**
 * The thing being typed, before it is an item.
 *
 * Validation lives here rather than in the modal so the rules can be tested
 * without mounting React, and so the board reuses the same length limits
 * when a staff member edits an item in place.
 *
 * Every update returns a new draft. Nothing here mutates its argument.
 *
 * Pure except `clientContext`, which reads the browser it is handed.
 */

import { IMPACT, IMPACT_META, TYPE, TYPE_META } from './feedbackTypes';
import { redactPath, screenFor } from './screens';

export const MAX_TITLE = 120;
export const MAX_DETAIL = 4000;
export const MIN_DETAIL = 15;

const isBlank = (value) => value === null || value === undefined || String(value).trim() === '';
const text = (value) => (isBlank(value) ? '' : String(value).trim());

/**
 * A blank draft. `type` and `impact` start unset on purpose: a prefilled
 * severity is a severity nobody chose, and the board would fill up with
 * whatever the default was.
 */
export function emptyDraft(overrides) {
  return {
    type: '',
    title: '',
    detail: '',
    impact: '',
    ...(overrides || {}),
  };
}

/** Immutable field update. */
export function updateDraft(draft, field, value) {
  return { ...(draft || emptyDraft()), [field]: value };
}

/**
 * What is wrong with this draft, by field.
 *
 * Messages are written to the person, not about the data -- "Say what you
 * expected to happen" beats "detail: min length 15".
 */
export function validateDraft(draft) {
  const row = draft || {};
  const errors = {};

  if (!TYPE_META[row.type]) {
    errors.type = 'Pick one so we know where this goes.';
  }

  const title = text(row.title);
  if (!title) {
    errors.title = 'A one-line summary, as you would say it out loud.';
  } else if (title.length > MAX_TITLE) {
    errors.title = `Keep it under ${MAX_TITLE} characters — the detail box is below.`;
  }

  const detail = text(row.detail);
  if (!detail) {
    errors.detail = 'Tell us what happened and what you expected instead.';
  } else if (detail.length < MIN_DETAIL) {
    errors.detail = 'A little more, please — enough for someone to act on it.';
  } else if (detail.length > MAX_DETAIL) {
    errors.detail = `That is over ${MAX_DETAIL} characters. Trim it, or attach the detail another way.`;
  }

  if (!IMPACT_META[row.impact]) {
    errors.impact = 'Pick one — it decides what gets looked at first.';
  }

  return { ok: Object.keys(errors).length === 0, errors };
}

/**
 * What the browser can say about itself without being asked.
 *
 * Deliberately narrow: enough to reproduce a rendering bug, nothing that
 * identifies a machine. No screen resolution, no language, no timezone.
 */
export function clientContext(win) {
  const w = win || (typeof window === 'undefined' ? null : window);
  if (!w) return { path: '', screen: '', screen_label: '', user_agent: '', viewport: '' };

  const pathname = (w.location && w.location.pathname) || '';
  const found = screenFor(pathname);
  const width = Number(w.innerWidth) || 0;
  const height = Number(w.innerHeight) || 0;

  return {
    path: redactPath(pathname),
    screen: found.id,
    screen_label: found.label,
    user_agent: text(w.navigator && w.navigator.userAgent).slice(0, 300),
    viewport: width && height ? `${width}x${height}` : '',
  };
}

/**
 * The body of the POST.
 *
 * `screen_label` is not sent -- the backend stores the id and the client
 * resolves the name, so renaming a screen does not require a migration.
 */
export function draftPayload(draft, context) {
  const row = draft || {};
  const ctx = context || {};

  return {
    type: row.type,
    title: text(row.title),
    detail: text(row.detail),
    impact: row.impact,
    screen: text(ctx.screen),
    path: text(ctx.path),
    user_agent: text(ctx.user_agent),
    viewport: text(ctx.viewport),
  };
}

/** Characters left in a field, never below zero for display purposes. */
export function remaining(value, max) {
  return Math.max(0, max - text(value).length);
}

/** Options for the two radio groups, in the order they are shown. */
export const TYPE_CHOICES = [TYPE.BUG, TYPE.IMPROVEMENT, TYPE.FEATURE].map((id) => ({
  id,
  ...TYPE_META[id],
}));

export const IMPACT_CHOICES = [IMPACT.BLOCKING, IMPACT.SLOWS, IMPACT.NICE].map((id) => ({
  id,
  ...IMPACT_META[id],
}));
